import { useEffect, useRef } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router';
import type { RefObject } from 'react';

import { useIsFirstRenderRef } from '#/hooks/useIsFirstRenderRef';
import { trackEvent } from '#/utils/analytics/track/trackEvent';
import { highlightElement } from '#/utils/highlightElement';

function useTrackJsPageViewOnNavigation({
  isFirstRenderRef,
  navigationType,
  pathname,
}: {
  isFirstRenderRef: RefObject<boolean>;
  navigationType: NavigationType;
  pathname: string;
}) {
  const isFirstRender = isFirstRenderRef.current;
  const lastPathnameRef = useRef(pathname);

  useEffect(() => {
    const lastPathname = lastPathnameRef.current;
    lastPathnameRef.current = pathname;

    if (
      isFirstRender ||
      navigationType === NavigationType.Replace ||
      lastPathname === pathname
    ) {
      return;
    }

    trackEvent('JS Page View');
  }, [isFirstRender, navigationType, pathname]);
}

function useHighlightHashTargetOnPushNavigation({
  hash,
  locationKey,
  navigationType,
}: {
  hash: string;
  locationKey: string;
  navigationType: NavigationType;
}) {
  useEffect(() => {
    // When clicking a href=#... we want to always scroll to it even if the same hash was already set
    if (!hash || navigationType !== NavigationType.Push) return;

    const target = document.getElementById(hash.substring(1));

    if (target) {
      highlightElement(target);
    }
  }, [hash, locationKey, navigationType]);
}

export function RenderingClientLocationHooks() {
  const { hash, key: locationKey, pathname } = useLocation();
  const navigationType = useNavigationType();

  const { isFirstRenderRef } = useIsFirstRenderRef();

  useTrackJsPageViewOnNavigation({
    isFirstRenderRef,
    navigationType,
    pathname,
  });
  useHighlightHashTargetOnPushNavigation({
    hash,
    locationKey,
    navigationType,
  });

  return null;
}
